/**
 * Tabla de rutas del SPA, con carga diferida por ruta.
 *
 * Cada vista se declara con `lazy` y se descarga cuando alguien navega a ella, no antes. Con
 * diecinueve vistas —siete del prototipo y doce nuevas—, varias con gráficos y una con la
 * generación de un PDF, el paquete único sería el peor costo de arranque de la aplicación y lo
 * pagaría por completo quien solo entra a registrar un vaso de agua desde el teléfono.
 *
 * El armazón y este archivo son lo único que va en el paquete inicial: los módulos de las
 * rutas se importan de forma diferida, así que ninguna vista entra por estar declarada.
 *
 * Convención de los módulos de ruta: cada uno exporta `Component`, que es el nombre que el
 * enrutador busca en el módulo diferido. Las tareas que crean vistas agregan una entrada aquí
 * y un archivo en `routes/`, sin tocar nada más.
 */

import { createBrowserRouter } from 'react-router';

import {
  RUTA_DE_AGUA,
  RUTA_DE_ALIMENTACION,
  RUTA_DE_CARDIO,
  RUTA_DE_COMIDAS,
  RUTA_DE_COMPOSICION,
  RUTA_DE_COMPRAS,
  RUTA_DE_CONSENTIMIENTOS,
  RUTA_DE_CORRELACION,
  RUTA_DE_DERECHOS_DE_DATOS,
  RUTA_DE_FUERZA,
  RUTA_DE_GLICEMIAS,
  RUTA_DE_INGRESO,
  RUTA_DE_LOGROS,
  RUTA_DE_PERFIL,
  RUTA_DE_PROGRESO,
  RUTA_DE_PROTEINA,
  RUTA_DE_RECETAS,
  RUTA_DE_RECORDATORIOS,
  RUTA_DE_RECUPERACION,
  RUTA_DE_REGISTRO,
  RUTA_DE_RESTABLECIMIENTO,
  RUTA_DEL_INFORME,
  RUTA_DEL_SENSOR,
} from './lib/rutas';
import { Armazon, Cargando, LimiteDeError } from './routes/armazon';

export const enrutador = createBrowserRouter([
  {
    path: '/',
    Component: Armazon,
    // Se muestra mientras viaja el fragmento de la primera vista, que es el único momento en
    // que la pantalla estaría en blanco.
    HydrateFallback: Cargando,
    ErrorBoundary: LimiteDeError,
    children: [
      { index: true, lazy: () => import('./routes/inicio') },

      // Sesión y consentimientos (tarea 4.11). Los caminos salen de `lib/rutas.ts` y no de un
      // literal, porque los mismos cuatro los escriben además los enlaces y la redirección que
      // dispara el `401`. Se declaran sin la barra inicial porque son hijos de `/`.
      { path: RUTA_DE_INGRESO.slice(1), lazy: () => import('./routes/ingreso') },
      { path: RUTA_DE_REGISTRO.slice(1), lazy: () => import('./routes/registro') },
      { path: RUTA_DE_RECUPERACION.slice(1), lazy: () => import('./routes/recuperarContrasena') },
      {
        path: RUTA_DE_RESTABLECIMIENTO.slice(1),
        lazy: () => import('./routes/restablecerContrasena'),
      },
      { path: RUTA_DE_CONSENTIMIENTOS.slice(1), lazy: () => import('./routes/consentimientos') },

      // Glicemias (tarea 5.9): serie con su tabla equivalente, registro manual, tendencia con la
      // HbA1c proyectada, insulina, importar y exportar. Es la vista más grande del producto y la
      // que más justifica la carga diferida: trae Recharts, que no viaja en el paquete inicial.
      { path: RUTA_DE_GLICEMIAS.slice(1), lazy: () => import('./routes/glicemias') },

      // Proteína y agua (tarea 7.6). Son vistas hermanas: cada una presenta un total del día, un
      // avance frente a una meta y una acción que vacía la fecha, y la de agua suma el histórico de
      // treinta días, que es lo único de las dos que trae Recharts.
      { path: RUTA_DE_PROTEINA.slice(1), lazy: () => import('./routes/proteina') },
      { path: RUTA_DE_AGUA.slice(1), lazy: () => import('./routes/agua') },

      // Composición corporal (tarea 10.7): asistente por pasos, valores derivados, evolución con su
      // tabla equivalente, comparación entre dos fechas y evaluaciones INBODY. Trae Recharts, que no
      // viaja en el paquete inicial, así que la carga diferida es la que evita que quien solo registra
      // un vaso de agua pague el gráfico.
      { path: RUTA_DE_COMPOSICION.slice(1), lazy: () => import('./routes/composicion') },

      // Sensor (tarea 6.15): consentimiento de LibreLinkUp, credenciales de la Cuenta_Seguidor,
      // selección de la conexión, instrucciones de invitación, estado del sensor con sus avisos y
      // desconexión. Cuelga de `/perfil` como hermana de los consentimientos, y es la vista que
      // menos gente va a abrir de todo el producto —se entra una vez a conectar y luego a mirar los
      // días restantes—, así que la carga diferida es la que evita que su flujo completo viaje en el
      // paquete inicial de quien nunca ha tenido un sensor.
      { path: RUTA_DEL_SENSOR.slice(1), lazy: () => import('./routes/sensor') },

      // Perfil y progreso (tarea 9.10). El perfil trae la información médica y la documentación
      // clínica; el progreso, la línea de tiempo por vista y la comparación lado a lado. Las dos
      // comparten el flujo de subida en tres pasos de `componentes/subida.tsx`, que viaja con ellas y
      // no en el paquete inicial: quien nunca adjunta un archivo no paga ni una línea de ese flujo.
      //
      // `/perfil` se declara **después** de sus dos hijas y da igual: React Router resuelve por
      // especificidad del patrón y no por orden de declaración, así que `/perfil/sensor` no cae en la
      // vista del perfil por estar registrada antes o después.
      { path: RUTA_DE_PERFIL.slice(1), lazy: () => import('./routes/perfil') },
      { path: RUTA_DE_PROGRESO.slice(1), lazy: () => import('./routes/progreso') },

      // Comidas y correlación (tarea 11.8). La de comidas trae el registro con fotografía, la marca
      // de ingesta, la asociación al plan y el historial del día con su adherencia; la de correlación,
      // las comidas ordenadas por variación y los patrones de alza por alimento. Las dos van
      // diferidas: la de comidas comparte con el perfil y el progreso el flujo de subida en tres
      // pasos de `componentes/subida.tsx`, que viaja con ellas y no en el paquete inicial.
      { path: RUTA_DE_COMIDAS.slice(1), lazy: () => import('./routes/comidas') },
      { path: RUTA_DE_CORRELACION.slice(1), lazy: () => import('./routes/correlacion') },

      // Fuerza y cardio (tarea 8.9). La de fuerza trae la evaluación inicial, el plan por
      // Ubicacion_Entrenamiento, la prescripción junto al resultado anterior, el registro por serie y la
      // evolución de carga; la de cardio, el rango de Zona 2, el registro con duración y frecuencia
      // promedio y la Semana_Objetivo. La de fuerza trae Recharts, que no viaja en el paquete inicial,
      // así que la carga diferida es la que evita que quien solo registra un vaso de agua pague el
      // gráfico. El camino de fuerza ya estaba declarado en `lib/rutas.ts` para el canal de
      // recordatorios, y aquí se reutiliza en lugar de escribirlo por segunda vez.
      { path: RUTA_DE_FUERZA.slice(1), lazy: () => import('./routes/fuerza') },
      { path: RUTA_DE_CARDIO.slice(1), lazy: () => import('./routes/cardio') },

      // Logros (tarea 13.8): rachas vigentes, rachas máximas, hitos obtenidos con su fecha y el
      // resumen de metas de una fecha del historial. Es la hermana del Dashboard, que es la única vista
      // del paquete inicial: esta va diferida porque se entra a ella desde el enlace del criterio 22.6
      // y no al abrir la aplicación, así que sus dos consultas y sus tablas no tienen por qué viajar
      // con la portada. No trae Recharts.
      { path: RUTA_DE_LOGROS.slice(1), lazy: () => import('./routes/logros') },

      // Alimentación, compras y recetas (tarea 12.13). La de alimentación trae el plan semanal de
      // siete días con sus cinco comidas, las restricciones aplicadas, la generación y el histórico;
      // la de compras, las líneas agrupadas por categoría con el estado de comprado y la exportación
      // en texto; la de recetas, el filtro por tipo, el aviso de las excluidas por intolerancias, el
      // detalle con sus pasos, el favorito y el registro como comida. Las tres van diferidas y
      // ninguna trae Recharts: lo que no viaja en el paquete inicial es la rejilla de las 35 comidas
      // de la semana, que es la vista más grande de las tres y la que menos gente abre a diario. El
      // camino de alimentación ya estaba declarado en `lib/rutas.ts` para el canal de recordatorios,
      // y aquí se reutiliza en lugar de escribirlo por segunda vez.
      { path: RUTA_DE_ALIMENTACION.slice(1), lazy: () => import('./routes/alimentacion') },
      { path: RUTA_DE_COMPRAS.slice(1), lazy: () => import('./routes/compras') },
      { path: RUTA_DE_RECETAS.slice(1), lazy: () => import('./routes/recetas') },

      // Derechos sobre los datos (tarea 16.10): la solicitud de exportación con el estado de la que
      // esté en curso y su enlace de descarga, y el flujo de eliminación de cuenta con la
      // confirmación explícita y el plazo comprometido. Cuelga de `/perfil` como hermana de los
      // consentimientos y del sensor, y va diferida como todas: es la vista que menos gente abre del
      // producto entero —se entra una vez a pedir el archivo, o una vez a irse—, así que su flujo
      // completo no tiene por qué viajar en el paquete inicial de nadie. No trae Recharts.
      { path: RUTA_DE_DERECHOS_DE_DATOS.slice(1), lazy: () => import('./routes/derechosDeDatos') },

      // Informe integral (tarea 14.9): la selección del período con su previsualización de promedios,
      // progresión y metas cumplidas, la confirmación del encabezado con el RUT y la generación con su
      // enlace de descarga de vigencia acotada. Va diferida como todas y no trae Recharts: la
      // previsualización se lee en tablas, que es lo que se compara fila a fila y lo que un lector de
      // pantalla recorre sabiendo qué está leyendo. El camino ya estaba declarado en `lib/rutas.ts`
      // para el enlace del Dashboard del criterio 22.6, y aquí se reutiliza en lugar de escribirlo por
      // segunda vez.
      { path: RUTA_DEL_INFORME.slice(1), lazy: () => import('./routes/informe') },

      // Recordatorios (tarea 15.8): el estado del permiso de notificaciones de este navegador, la
      // activación de los cuatro tipos con sus horas, la ventana de silencio y la reactivación de una
      // suscripción que el servicio push declaró caduca. Va diferida como todas y no trae Recharts: lo
      // que no viaja en el paquete inicial es el flujo de activación push completo, que solo paga quien
      // entra a configurar sus recordatorios. El camino ya estaba declarado en `lib/rutas.ts` para el
      // enlace del Dashboard del criterio 25.2, y aquí se reutiliza en lugar de escribirlo por segunda
      // vez.
      { path: RUTA_DE_RECORDATORIOS.slice(1), lazy: () => import('./routes/recordatorios') },

      // Las demás rutas del producto se agregan aquí, una por tarea, en este mismo formato.
      // El orden no importa salvo para el comodín, que va último por definición.

      { path: '*', lazy: () => import('./routes/noEncontrado') },
    ],
  },
]);
